import axios, { AxiosError, type AxiosResponse } from 'axios'
import cx from 'classnames'
import { type GetStaticPropsResult } from 'next'
import Head from 'next/head'
import { useCallback, useId, useMemo, useState, type FormEvent } from 'react'
import { useRouter } from 'next/router'

import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getImageDimensions } from '@lib/image'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext } from '@lib/routes'
import { getSite } from '@lib/sanity/site'
import {
  type VerifySitePasswordPayload,
  type VerifySitePasswordResponse,
} from '@lib/site-access'

import Button, { ButtonVariant } from '@components/buttons/button'
import Photo from '@components/photo'

type SitePasswordPageProps = BasePageProps & {
  site: SanitySiteFragment
}

const SitePasswordPage = ({ locale, site }: SitePasswordPageProps) => {
  const router = useRouter()

  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const redirect = useMemo(() => {
    // Get redirect URL from query params or default to homepage
    const redirect = router.query.redirect as string | undefined

    return redirect ?? '/'
  }, [router])

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      setError(null)
      setIsLoading(true)

      try {
        await axios.post<
          VerifySitePasswordResponse,
          AxiosResponse<VerifySitePasswordResponse>,
          VerifySitePasswordPayload
        >(
          '/api/verify-site-password',
          {
            password,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Locale': locale,
            },
          },
        )

        router.push(redirect)
      } catch (error) {
        setIsLoading(false)

        if (
          error &&
          error instanceof AxiosError &&
          error.response?.data?.error === 'incorrect_password'
        ) {
          setError(
            site.strings?.passwordProtectionIncorrectPasswordError ?? null,
          )
          return
        }

        setError(site.strings?.passwordProtectionError ?? null)
      }
    },
    [locale, password, redirect, router, site.strings],
  )

  const passwordInputId = useId()

  const logo = site.identitySettings?.logo
  const logoDimensions = logo ? getImageDimensions(logo) : undefined

  return (
    <>
      <Head>
        <title>{site.strings?.passwordProtectionMetaTitle}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <header
        className={cx(
          'fixed top-0 inset-x-0 z-50 bg-header-bg text-header-text',
          'flex justify-center items-center w-full py-5 px-3 border-b border-gray',
        )}
      >
        {!!logo && (
          <Photo
            image={logo}
            width={logoDimensions?.width}
            height={logoDimensions?.height}
            priority
            className="relative inline-block overflow-hidden"
            imageClassName="block absolute inset-0 m-auto min-w-[100%] max-w-[100%] min-h-[100%] max-h-[100%] w-0 h-0 overflow-hidden"
            showPlaceholder={false}
            style={{
              width: logoDimensions?.width,
              height: logoDimensions?.height,
            }}
          />
        )}
      </header>

      <div
        className={cx(
          'w-screen min-h-screen',
          'bg-cover bg-no-repeat bg-top',
          'sm:flex sm:justify-center sm:items-center sm:overflow-y-auto',
        )}
        style={{
          backgroundImage: 'url(/images/password-page-background.webp)',
        }}
      >
        <form
          onSubmit={handleSubmit}
          className="mx-auto py-24 max-w-[480px] sm:w-[480px] sm:mx-0 sm:py-12 px-12 space-y-8 text-white"
        >
          <div className="space-y-4">
            <h1 className="text-4xl text-center">
              {site.strings?.passwordProtectionTitle}
            </h1>
            <p className="text-2xl text-center">
              {site.strings?.passwordProtectionDescription}
            </p>
          </div>

          <div className="relative space-y-1">
            {!!site.strings?.passwordProtectionPasswordLabel && (
              <label htmlFor={passwordInputId} className="text-xs uppercase">
                {site.strings?.passwordProtectionPasswordLabel}
              </label>
            )}

            <input
              id={passwordInputId}
              type="password"
              name="password"
              value={password}
              placeholder={site.strings?.passwordProtectionPasswordPlaceholder}
              disabled={isLoading}
              onChange={(event) => {
                setPassword(event.target.value)
                setError(null)
              }}
              onBlur={(event) => {
                setPassword(event.target.value)
                setError(null)
              }}
              className={cx(
                'relative appearance-none w-full h-full py-4 px-3 border text-sm leading-none',
                'bg-white text-input-text',
                {
                  'border-input-border': !error,
                  'border-error': !!error,
                },
              )}
            />

            {!!error && (
              <div role="alert" className="text-xs text-white">
                {error}
              </div>
            )}
          </div>

          <Button
            type="submit"
            variant={ButtonVariant.FILLED}
            disabled={isLoading}
            className="w-full"
          >
            {site.strings?.passwordProtectionSubmitButton}
          </Button>
        </form>
      </div>
    </>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<SitePasswordPageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const site = await getSite(sanityClient, context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      site: getNonNullValues(site),
    },
    revalidate: 600,
  }
}

export default SitePasswordPage
